import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import client from "../api/client";
import { useMeta } from "../context/MetaContext";
import SimpleTextEditor from "../components/SimpleTextEditor";

function parseJson(value) {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function formatDowntime(minutes) {
  if (minutes === null || minutes === undefined) return "in progress";
  const hrs = Math.floor(minutes / 60);
  const min = minutes % 60;
  return `${hrs}h ${min}m`;
}

export default function PreviewPage({ websiteId }) {
  const { assessmentTemplate, metaLoading, metaError } = useMeta();
  const assessmentStages = assessmentTemplate?.stages || [];
  const [mainTab, setMainTab] = useState("assessment");
  const [selectedStage, setSelectedStage] = useState("");
  const [questionnaire, setQuestionnaire] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [answerIncidentLinks, setAnswerIncidentLinks] = useState({});
  const [popupIncident, setPopupIncident] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 120, y: 120 });
  const [isDraggingPopup, setIsDraggingPopup] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [notes, setNotes] = useState({});
  const [noteDraft, setNoteDraft] = useState("");
  const [savedAt, setSavedAt] = useState("");
  const [focusMode, setFocusMode] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);

  const load = async () => {
    if (!websiteId) return;
    const [qRes, iRes] = await Promise.all([
      client.get(`/websites/${websiteId}/questionnaires`),
      client.get(`/websites/${websiteId}/incidents`)
    ]);
    setQuestionnaire(qRes.data.data || []);
    setIncidents(iRes.data.data || []);
  };

  useEffect(() => {
    load();
  }, [websiteId]);

  useEffect(() => {
    if (!assessmentStages.length) return;
    if (!selectedStage || !assessmentStages.some((item) => item.key === selectedStage)) {
      setSelectedStage(assessmentStages[0].key);
    }
  }, [assessmentStages, selectedStage]);

  useEffect(() => {
    const loadEvidence = async () => {
      if (!websiteId || mainTab !== "assessment" || !selectedStage) return;
      const { data } = await client.get(`/websites/${websiteId}/evidence`, { params: { stage_key: selectedStage } });
      const grouped = {};
      (data.answer_incidents || []).forEach((row) => {
        if (!grouped[row.question_key]) grouped[row.question_key] = [];
        grouped[row.question_key].push(Number(row.incident_id));
      });
      setAnswerIncidentLinks(grouped);
    };
    loadEvidence();
  }, [websiteId, selectedStage, mainTab]);

  useEffect(() => {
    const handleMove = (event) => {
      if (!isDraggingPopup) return;
      setPopupPosition({
        x: Math.max(10, event.clientX - dragOffsetRef.current.x),
        y: Math.max(10, event.clientY - dragOffsetRef.current.y),
      });
    };
    const handleUp = () => setIsDraggingPopup(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [isDraggingPopup]);

  const loadNotes = async () => {
    if (!websiteId) {
      setNotes({});
      return {};
    }
    try {
      const { data } = await client.get(`/websites/${websiteId}/preview-notes`);
      const map = data?.notes && typeof data.notes === "object" ? data.notes : {};
      setNotes(map);
      return map;
    } catch {
      setNotes({});
      return {};
    }
  };

  useEffect(() => {
    loadNotes();
  }, [websiteId]);

  const stageMap = useMemo(() => {
    const map = new Map();
    questionnaire.forEach((entry) => map.set(entry.stage_key, entry));
    return map;
  }, [questionnaire]);

  const stage = assessmentStages.find((item) => item.key === selectedStage) || assessmentStages[0] || { key: "", label: "", description: "", questions: [] };
  const stageRow = stageMap.get(stage.key);
  const stageAnswers = parseJson(stageRow?.responses_json);
  const noteKey = mainTab === "assessment" ? `assessment:${stage.key}` : "incidents:all";

  useEffect(() => {
    setNoteDraft(notes[noteKey] || "");
  }, [notes, noteKey]);

  const assessmentMetrics = useMemo(() => {
    const completed = questionnaire.filter((item) => Number(item.completion_percent || 0) > 0).length;
    const total = assessmentStages.length;
    return { completed, total };
  }, [assessmentStages.length, questionnaire]);

  const incidentMetrics = useMemo(() => {
    const open = incidents.filter((i) => i.status === "open").length;
    const investigating = incidents.filter((i) => i.status === "investigating").length;
    const resolved = incidents.filter((i) => i.status === "resolved").length;
    return { open, investigating, resolved, total: incidents.length };
  }, [incidents]);
  const incidentsById = useMemo(() => {
    const map = new Map();
    incidents.forEach((incident) => map.set(Number(incident.id), incident));
    return map;
  }, [incidents]);

  const saveNote = async () => {
    if (!websiteId) return;
    const next = { ...notes, [noteKey]: noteDraft };
    setNotes(next);
    setNoteSaving(true);
    try {
      await client.post(`/websites/${websiteId}/preview-notes`, { notes: next });
      setSavedAt(new Date().toLocaleTimeString());
    } finally {
      setNoteSaving(false);
    }
  };

  const clearNote = () => {
    setNoteDraft("");
  };

  if (!websiteId) {
    return <main className="page-grid"><section className="card-modern"><p>Select a website from top switcher to open previews.</p></section></main>;
  }

  if (metaLoading || !assessmentStages.length || !stage?.key) {
    return (
      <main className="page-grid">
        <section className="card-modern">
          <p>{metaError || "Loading preview template from database..."}</p>
        </section>
      </main>
    );
  }

  return (
    <main className={`page-grid ${focusMode ? "preview-focus-mode" : ""}`}>
      <section className="card-modern">
        <div className="tab-row preview-toolbar">
          <button className={`tab-btn ${mainTab === "assessment" ? "active" : ""}`} onClick={() => setMainTab("assessment")}>Discussion Points Preview</button>
          <button className={`tab-btn ${mainTab === "incidents" ? "active" : ""}`} onClick={() => setMainTab("incidents")}>Incident Preview</button>
          <button className={`tab-btn ${focusMode ? "active" : ""}`} onClick={() => setFocusMode((prev) => !prev)}>
            {focusMode ? "Exit Focus Mode" : "Focus Mode"}
          </button>
          <Link to="/tools/wpa/dashboard/reports" className="button-link ghost preview-report-link">Open Reports</Link>
        </div>
        <p className="muted">Review answers in detail, write your own analyst notes, and then generate the report.</p>
      </section>

      {mainTab === "assessment" ? (
        <>
          <section className="stats-row">
            <article className="stat-card"><p>Stages With Data</p><strong>{assessmentMetrics.completed}/{assessmentMetrics.total}</strong></article>
            <article className="stat-card"><p>Current Stage</p><strong>{stage.label.split(".")[0]}</strong></article>
            <article className="stat-card"><p>Stage Progress</p><strong>{stageRow?.completion_percent || 0}%</strong></article>
            <article className="stat-card"><p>Answers Captured</p><strong>{Object.values(stageAnswers).filter((v) => String(v || "").trim()).length}</strong></article>
          </section>

          <section className="card-modern preview-layout">
            <aside className="preview-stage-nav">
              <h3>Stages</h3>
              {assessmentStages.map((entry) => {
                const row = stageMap.get(entry.key);
                return (
                  <button
                    key={entry.key}
                    className={`stage-item ${selectedStage === entry.key ? "active" : ""}`}
                    onClick={() => setSelectedStage(entry.key)}
                  >
                    <span>{entry.label}</span>
                    <strong>{row?.completion_percent || 0}%</strong>
                  </button>
                );
              })}
            </aside>

            <div className="preview-stage-content">
              <div className="preview-head">
                <h3>{stage.label}</h3>
                <span className="progress-pill">{stageRow?.completion_percent || 0}%</span>
              </div>
              <p className="muted">{stage.description}</p>

              <div className="qa-grid">
                {stage.questions.map((question, index) => {
                  const key = `q${index + 1}`;
                  return (
                    <article key={key} className="answer-card">
                      <p className="q-title">{index + 1}. {question}</p>
                      <p className="a-value">{stageAnswers[key] || "No response provided yet."}</p>
                      {(answerIncidentLinks[key] || []).length ? (
                        <div className="incident-link-row">
                          <span className="muted">Linked incidents:</span>
                          {(answerIncidentLinks[key] || []).map((incidentId) => (
                            <button
                              key={incidentId}
                              type="button"
                              className="incident-pill-link"
                              onClick={() => {
                                const incident = incidentsById.get(Number(incidentId));
                                if (incident) {
                                  setPopupIncident(incident);
                                  setPopupPosition({ x: 140, y: 140 });
                                }
                              }}
                            >
                              Incident #{incidentId}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>

              <section className="review-notes-card">
                <div className="section-head">
                  <h4>Your Notes For This Stage</h4>
                  {savedAt ? <span className="muted">Saved at {savedAt}</span> : null}
                </div>
                <SimpleTextEditor
                  placeholder="Write your own observations, reviewer comments, and additional recommendations..."
                  value={noteDraft}
                  onChange={setNoteDraft}
                  ariaLabel="preview-stage-notes"
                />
                <div className="hero-actions">
                  <button type="button" className="button-link" onClick={saveNote} disabled={noteSaving}>
                    {noteSaving ? "Saving..." : "Save Notes"}
                  </button>
                  <button type="button" className="button-link ghost" onClick={clearNote}>Clear Draft</button>
                </div>
              </section>
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="stats-row">
            <article className="stat-card"><p>Total Incidents</p><strong>{incidentMetrics.total}</strong></article>
            <article className="stat-card"><p>Open</p><strong>{incidentMetrics.open}</strong></article>
            <article className="stat-card"><p>Investigating</p><strong>{incidentMetrics.investigating}</strong></article>
            <article className="stat-card"><p>Resolved</p><strong>{incidentMetrics.resolved}</strong></article>
          </section>

          <section className="card-modern">
            <h3>Incident Timeline Preview</h3>
            <div className="preview-list compact">
              {incidents.map((incident) => (
                <article key={incident.id} className="preview-item">
                  <div className="preview-head">
                    <h4>{incident.title}</h4>
                    <span className={`status-chip status-${incident.status}`}>{incident.status}</span>
                  </div>
                  <div className="incident-meta-grid">
                    <p><strong>Severity:</strong> {incident.severity}</p>
                    <p><strong>Occurred:</strong> {incident.occurred_at}</p>
                    <p><strong>Resolved:</strong> {incident.resolved_at || "Not resolved"}</p>
                    <p><strong>Downtime:</strong> {formatDowntime(incident.downtime_minutes)}</p>
                  </div>
                  <p><strong>Issue:</strong> {incident.issue_summary || incident.details || "-"}</p>
                  <p><strong>Root Cause:</strong> {incident.root_cause || "-"}</p>
                  <p><strong>Business Impact:</strong> {incident.business_impact || "-"}</p>
                  <p><strong>Resolution Notes:</strong> {incident.resolution_notes || "-"}</p>
                </article>
              ))}
              {!incidents.length ? <p className="muted">No incidents recorded yet for this website.</p> : null}
            </div>
          </section>

          <section className="card-modern review-notes-card">
            <div className="section-head">
              <h4>Your Incident Review Notes</h4>
              {savedAt ? <span className="muted">Saved at {savedAt}</span> : null}
            </div>
            <SimpleTextEditor
              placeholder="Write your incident-level summary, cross-cutting risks, and recommendations..."
              value={noteDraft}
              onChange={setNoteDraft}
              ariaLabel="preview-incident-notes"
            />
            <div className="hero-actions">
              <button type="button" className="button-link" onClick={saveNote} disabled={noteSaving}>
                {noteSaving ? "Saving..." : "Save Notes"}
              </button>
              <button type="button" className="button-link ghost" onClick={clearNote}>Clear Draft</button>
            </div>
          </section>
        </>
      )}

      {popupIncident ? (
        <section className="incident-popup" style={{ left: popupPosition.x, top: popupPosition.y }}>
          <header
            className="incident-popup-head"
            onMouseDown={(event) => {
              setIsDraggingPopup(true);
              dragOffsetRef.current = {
                x: event.clientX - popupPosition.x,
                y: event.clientY - popupPosition.y
              };
            }}
          >
            <strong>{popupIncident.title}</strong>
            <button type="button" className="button-link ghost" onClick={() => setPopupIncident(null)}>Close</button>
          </header>
          <div className="incident-popup-body">
            <p><strong>Severity:</strong> {popupIncident.severity}</p>
            <p><strong>Status:</strong> {popupIncident.status}</p>
            <p><strong>Occurred:</strong> {popupIncident.occurred_at}</p>
            <p><strong>Issue:</strong> {popupIncident.issue_summary || popupIncident.details || "-"}</p>
            <p><strong>Root Cause:</strong> {popupIncident.root_cause || "-"}</p>
            <p><strong>Business Impact:</strong> {popupIncident.business_impact || "-"}</p>
            <p><strong>Resolution:</strong> {popupIncident.resolution_notes || "-"}</p>
          </div>
        </section>
      ) : null}
    </main>
  );
}
